import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { getFilterCache, setFilterCache } from '../axios/axiosAuth';
import useTimeRangeOptions from '../hooks/useTimeRangeOptions';

const FilterContext = createContext();

const FilterProvider = ({ children }) => {
  const { timeRanges } = useTimeRangeOptions();
  const [filter, setFilter] = useState(() => {
    const filterCache = getFilterCache();
    if (filterCache?.monitoring) {
      const oldMon = filterCache.monitoring;
      const newMon = Object.fromEntries(
        Object.entries(oldMon).map(([key, val]) => {
          if (key === 'timeRange') {
            return [key, timeRanges[val.key]];
          }
          if (val && typeof val === 'object' && val.timeRange) {
            return [key, { ...val, timeRange: timeRanges[val.timeRange.key] }];
          }
          return [key, val];
        }),
      );
      filterCache.monitoring = newMon;
    }
    return filterCache ?? {};
  });
  const getFilterData = useCallback(
    filterIndex => filter[filterIndex],
    [filter],
  );
  const setFilterData = useCallback(
    (filterIndex, data) => {
      const newFilter = {
        ...filter,
        [filterIndex]: { ...(filter[filterIndex] ?? {}), ...data },
      };
      setFilter(newFilter);
      setFilterCache(newFilter);
    },
    [filter, setFilter],
  );

  const value = useMemo(
    () => ({
      getFilterData,
      setFilterData,
    }),
    [getFilterData, setFilterData],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export const useFilter = () => {
  return useContext(FilterContext);
};

FilterProvider.defaultProps = {
  children: null,
};

FilterProvider.propTypes = {
  children: PropTypes.element,
};

export default FilterProvider;
